import * as React from 'react';
import { FC, useCallback, useEffect, useState } from 'react';
import { ListGroup, ListGroupItem, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { ClientEntity } from '../../store/entities/state';
import { WaitingListItem } from '../../models';
import { addToWaitingListAsync, getWaitingListByClientAsync, removeFromWaitingListAsync } from '../../ajax/requests';
import { useGeneralSettings } from '../../store/settings/hooks';
import { serviceName } from '../../shared/appointmint/appointment';
import Typography from '../../components/Typography';
import WaitingListItemForm from '../../components/Forms/WaitingListItemForm';
import styled from 'styled-components';
import AppButton from '../../components/AppButton';

export interface ClientWaitingListProps {
    isOpen?: boolean;
    client: ClientEntity;
}

const ServiceGroup = styled.div<{ children?: React.ReactNode }>`
    margin-bottom: var(--spacer-M);
`;

const ServiceHeader = styled.div`
    font-weight: bold;
    padding: var(--spacer-XS) 0;
    margin-bottom: var(--spacer-XS);
    border-bottom: var(--border-thin) solid var(--appLigthGrey);
    color: var(--text-body);
`;

const EntryRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    width: 100%;
`;

const EntryMeta = styled.div`
    font-size: 0.8em;
    color: var(--text-muted);
`;

const ClientWaitingList: FC<ClientWaitingListProps> = ({ isOpen = true, client }) => {
    const t = useTranslations();
    const generalSettings = useGeneralSettings();

    const [items, setItems] = useState<WaitingListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [formValue, setFormValue] = useState<Partial<WaitingListItem>>({
        clientId: client.id,
        clientName: client.name,
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setLoading(true);
        getWaitingListByClientAsync(client.id)
            .then(setItems)
            .finally(() => setLoading(false));
    }, [isOpen, client.id]);

    const openModal = useCallback(() => {
        setFormValue({ clientId: client.id, clientName: client.name });
        setModalOpen(true);
    }, [client.id, client.name]);

    const handleAdd = useCallback(async () => {
        if (!formValue.clientId || !formValue.service) return;
        setSaving(true);
        try {
            await addToWaitingListAsync(formValue as WaitingListItem);
            const updated = await getWaitingListByClientAsync(client.id);
            setItems(updated);
            setModalOpen(false);
        } finally {
            setSaving(false);
        }
    }, [formValue, client.id]);

    const handleRemove = useCallback((item: WaitingListItem) => async () => {
        await removeFromWaitingListAsync(item.clientId, item.service, item.professionalId);
        setItems(prev => prev.filter(i => !(i.clientId === item.clientId && i.service === item.service && i.professionalId === item.professionalId)));
    }, []);

    const grouped = items.reduce<Record<string, WaitingListItem[]>>((acc, item) => {
        const key = item.service;
        acc[key] = [...(acc[key] ?? []), item];
        return acc;
    }, {});

    return (
        <>
            <div className="topButtonsContainer">
                <AppButton onClick={openModal}>{t(TK.addToWaitingList)}</AppButton>
            </div>

            {loading
                ? <Typography><i>{t(TK.loading)}...</i></Typography>
                : !items.length
                    ? <Typography><i>{t(TK.noEntries)}</i></Typography>
                    : Object.entries(grouped).map(([service, entries]) => (
                        <ServiceGroup key={service}>
                            <ServiceHeader>{serviceName(service, generalSettings)}</ServiceHeader>
                            <ListGroup>
                                {entries.map((item, idx) => (
                                    <ListGroupItem key={idx}>
                                        <EntryRow>
                                            <div>
                                                {item.professionalName && <div><b>{item.professionalName}</b></div>}
                                                <EntryMeta>
                                                    {t(TK.addedBy)}: {item.addedByName}
                                                    {item.addedAt && <> · {item.addedAt.substring(0, 10)}</>}
                                                </EntryMeta>
                                                {item.notes && <div style={{ marginTop: 'var(--spacer-XS)' }}>{item.notes}</div>}
                                            </div>
                                            <AppButton color="link" size="sm" onClick={handleRemove(item)}>
                                                {t(TK.remove)}
                                            </AppButton>
                                        </EntryRow>
                                    </ListGroupItem>
                                ))}
                            </ListGroup>
                        </ServiceGroup>
                    ))
            }

            <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)}>
                <ModalHeader toggle={() => setModalOpen(false)}>{t(TK.addToWaitingList)}</ModalHeader>
                <ModalBody>
                    <WaitingListItemForm value={formValue} onChange={setFormValue} />
                </ModalBody>
                <ModalFooter>
                    <AppButton
                        color="success"
                        disabled={!formValue.clientId || !formValue.service || saving}
                        onClick={handleAdd}
                    >
                        {t(TK.add)}
                    </AppButton>
                    <AppButton color="secondary" onClick={() => setModalOpen(false)}>
                        {t(TK.cancel)}
                    </AppButton>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default ClientWaitingList;
